import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo } from 'react';

import Breadcrumb from 'shopper/components/Breadcrumb/V1/Breadcrumb';
import Heading from 'shopper/components/Heading';
import Text from 'shopper/components/Text';

import BreadcrumbItem from 'components/Breadcrumb/Item';
import TextWithRichContent from 'components/TextWithRichContent';

import { toCategoryLink, toSubcategoryLink } from 'lib/links';
import placeholder from 'lib/placeholder';

import LINKS from 'constants/links';

const TextWithEllipsisWithShowMore = dynamic(
  () => import('components/TextWithEllipsisWithShowMore')
);

const NOT_EXPANDED_DESCRIPTION_LIMIT = 185;

/**
 * This fallback fn should be kept until the introductions of all the
 * subcategories and deep subcategories are registered on the backend.
 */
const toFallbackIntroText = (categoryName) =>
  `Confira nossa seleção com as melhores promoções de ${categoryName.trim()}. Não encontrou o que procura? Adicione o item à sua Lista de Desejos que avisamos assim que tiver uma oferta boa de verdade!`;

const descriptionTextIsExpandable = (description) =>
  description && description.length > NOT_EXPANDED_DESCRIPTION_LIMIT;

const CategoryHeading = ({
  categoryIntroText = null,
  categoryName,
  categorySlug,
  deepSubcategoryName = null,
  subcategoryName = null,
  subcategorySlug = null,
  title,
}) => (
  <div className="mb-4">
    <Breadcrumb>
      <BreadcrumbItem href={LINKS.HOME}>
        {placeholder('breadcrumbs.app')}
      </BreadcrumbItem>
      {/* This is not validated as one-to-all conditional because
          `Breadcrumb` currently needs to receive each children in
           separated components, not as one unique fragment */}
      {!subcategoryName ? (
        <BreadcrumbItem>{categoryName}</BreadcrumbItem>
      ) : (
        <BreadcrumbItem href={toCategoryLink(categorySlug)}>
          {categoryName}
        </BreadcrumbItem>
      )}
      {subcategoryName &&
        (!deepSubcategoryName ? (
          <BreadcrumbItem>{subcategoryName}</BreadcrumbItem>
        ) : (
          <BreadcrumbItem href={toSubcategoryLink(subcategorySlug)}>
            {subcategoryName}
          </BreadcrumbItem>
        ))}
      {deepSubcategoryName && (
        <BreadcrumbItem>{deepSubcategoryName}</BreadcrumbItem>
      )}
    </Breadcrumb>
    <Heading as="h1" size="size3">
      {title}
    </Heading>
    {(deepSubcategoryName || subcategoryName) &&
      (descriptionTextIsExpandable(categoryIntroText) ? (
        <TextWithEllipsisWithShowMore
          as="div"
          className="max-w-[730px] pt-2"
          lineClampClassName="line-clamp-2"
          initialShowSeeMoreLinkState
        >
          {categoryIntroText ? (
            <TextWithRichContent as="div">
              {categoryIntroText}
            </TextWithRichContent>
          ) : (
            toFallbackIntroText(subcategoryName || deepSubcategoryName)
          )}
        </TextWithEllipsisWithShowMore>
      ) : (
        <Text as="span" className="line-clamp-2 max-w-[730px] pt-2">
          {categoryIntroText ? (
            <TextWithRichContent as="div">
              {categoryIntroText}
            </TextWithRichContent>
          ) : (
            toFallbackIntroText(subcategoryName || deepSubcategoryName)
          )}
        </Text>
      ))}
  </div>
);

CategoryHeading.propTypes = {
  categoryIntroText: PropTypes.string,
  categoryName: PropTypes.string.isRequired,
  categorySlug: PropTypes.string.isRequired,
  deepSubcategoryName: PropTypes.string,
  subcategoryName: PropTypes.string,
  subcategorySlug: PropTypes.string,
  title: PropTypes.string.isRequired,
};

export default memo(CategoryHeading);
